import { TableModal } from "@features/ax/shared/tableModal";
import { getCriticalityLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { TooltipProvider } from "@shared/ui/tooltip";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllSystems } from "../assetRegistry/systems/hooks";

interface FilterSystemProps {
  showCriticalAssetsModal: boolean;
  setShowCriticalAssetsModal: any;
  title?: string;
  filters?: Record<string, any>;
}

const getVariant = (value: string): "destructive" | "secondary" | "outline" => {
  switch (value) {
    case "Very High Priority":
    case "Critical":
      return "destructive";
    case "Medium Priority":
      return "secondary";
    case "Low Priority":
      return "outline";
    default:
      return "outline";
  }
};

export const FilterSystemModal: React.FC<FilterSystemProps> = ({
  showCriticalAssetsModal,
  setShowCriticalAssetsModal,
  title,
  filters,
}) => {
  useEffect(() => {
    logEvent("alerts_tab_viewed");
  }, []);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();
  const currency = useCurrency();
  const [page, setPage] = useState(1);
  const { data: systemList } = useGetAllSystems(filters);
  const startItem = (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, systemList?.count);

  const pagination = {
    currentPage: page,
    totalPages: Math.ceil(systemList?.count / PAGE_SIZE),
    totalItems: systemList?.count,
    startItem: startItem,
    endItem: endItem,
  };
  const columns: TableColumn[] = useMemo(
    () => [
      {
        key: "systemNo",
        header: "System ID",
        type: "text",
        sortable: true,
        headerTooltip: "The unique identifier for this system.",
      },
      {
        key: "systemName",
        header: "System Name",
        type: "text",
        sortable: true,
        headerTooltip: "The common name used to identify this system.",
      },
      {
        key: "systemTypeDisplay",
        header: "System Type",
        type: "text",
        sortable: true,
        headerTooltip: "The classification or category of this system.",
      },
      {
        key: "location",
        header: "Location",
        type: "custom",
        headerTooltip: "The specific physical location or zone of the system.",
        render: (value) => <HoverRevealText text={value} />,
      },
      {
        key: "facilityName",
        header: "Facility",
        type: "text",
        headerTooltip:
          "The main facility where this system is located or managed from.",
        render: (value) => value || "N/A",
      },
      {
        key: "commissionedDate",
        header: "Commission Date",
        type: "text",
        headerTooltip:
          "The date this system was officially commissioned or entered service.",
      },
      {
        key: "criticalityLevelDisplay",
        header: "Criticality",
        headerTooltip:
          "An assessment of the system's importance. Higher criticality means its failure has a greater impact.",
        type: "custom",
        render: (value: any, row: any) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.criticalityScore}</span>
            <Badge
              variant={getVariant(
                getCriticalityLabel(row.criticalityScore).label
              )}
              className={getCriticalityLabel(row.criticalityScore).color}
            >
              {getCriticalityLabel(row.criticalityScore).label}
            </Badge>
          </div>
        ),
      },
      {
        key: "designCapacity",
        header: "Design Capacity",
        headerTooltip:
          "The maximum output, flow, or volume the system was designed to handle.",
        type: "text",
      },
      {
        key: "currentThroughput",
        header: "Current Capacity",
        headerTooltip:
          "The current or most recent operational output of the system. This data is updated in near real-time.",
        type: "text",
      },
      {
        key: "statusDisplay",
        header: "Operational Status",
        type: "badge",
        sortable: true,
        headerTooltip:
          "The current real-time operational status of the system.",
        badgeConfig: {
          getVariant: (value: string) => {
            switch (value) {
              case "Operational":
                return "default";
              case "Under Maintenance":
                return "secondary";
              case "Decommissioned":
                return "destructive";
              default:
                return "outline";
            }
          },
        },
      },
    ],
    []
  );

  const handleViewAllCriticalAssets = () => {
    setShowCriticalAssetsModal(true);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive" as const;
      case "medium":
        return "secondary" as const;
      case "low":
        return "default" as const;
      default:
        return "outline" as const;
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div>
            {/* <PageHeader
              title="Active Alerts"
              subtitle={`Critical events, potential failures, and compliance issues requiring immediate attention for ${facility.facilityName}`}
            /> */}
          </div>
        </div>
      </div>
      <TableModal
        isOpen={showCriticalAssetsModal}
        title={title ? title : "Systems with Highest Risk"}
        onClose={() => setShowCriticalAssetsModal(false)}
        columns={columns}
        data={systemList?.results ?? []}
        loading={false}
        searchPlaceholder="Search Systems..."
        pagination={pagination}
        onPageChange={setPage}
      />
    </TooltipProvider>
  );
};
